import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert, AlertColor } from "@mui/material";
import { Z } from "../theme/zIndex";

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  autoHideDuration: number;
}

interface NotifyOptions {
  severity?: AlertColor;
  autoHideDuration?: number;
}

interface NotificationsContextType {
  notify: (message: string, options?: NotifyOptions) => void;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] =
    useState<Notification | null>(null);

  const notify = useCallback((message: string, options: NotifyOptions = {}) => {
    const notification: Notification = {
      id: Date.now().toString(),
      message,
      severity: options.severity || "info",
      autoHideDuration: options.autoHideDuration || 2500,
    };

    setNotifications((prev) => [...prev, notification]);
  }, []);

  const handleClose = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  const handleExited = useCallback(() => {
    setNotifications((prev) => {
      const newNotifications = prev.slice(1);
      setCurrentNotification(newNotifications[0] || null);
      return newNotifications;
    });
  }, []);

  // Procesar la cola de notificaciones
  React.useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      setCurrentNotification(notifications[0]);
    }
  }, [notifications]);

  return (
    <NotificationsContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={!!currentNotification}
        autoHideDuration={currentNotification?.autoHideDuration}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={(theme) => ({
          zIndex: Math.max(theme.zIndex.modal, Z.modal) + 2000,
        })}
      >
        {currentNotification ? (
          <Alert
            elevation={6}
            variant="filled"
            severity={currentNotification.severity}
            onClose={handleClose}
          >
            {currentNotification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationsContext.Provider>
  );
};

export const useNotify = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotify must be used within a NotificationsProvider");
  }
  return context;
};
